import React, { useState } from "react";

//UI Material
import { Box, Card, Divider, Grid, Stack, Tab, Tabs } from "@mui/material";

//Helpers
import { checkOrderStatus } from "../../Helpers/orderFunctions";
import {
  typographyForCustomizedFontSize,
  typographyForMainTitles,
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";
import { selectMonth } from "../../Helpers/date";
import { evstations } from "../../Helpers/productSelector";

//Function to generate tabs
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sales-product-tabpanel-${index}`}
      aria-labelledby={`sales-product-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{typographyForText(children)}</Box>}
    </div>
  );
}

export default function SalesCard({ data, year }) {
  // ==========> Component States <==========
  // State for tab value
  const [tabValue, setTabValue] = useState(0);

  // ==========> Component Functions <==========
  //Function to set tab value
  const handleChange = (_event, newValue) => {
    setTabValue(newValue);
  };
  //Constant for change tab arrow funcion
  const changeTab = handleChange;

  //Function for giving an index to each tab
  function a11yProps(index) {
    return {
      id: `sales-product-tab-${index}`,
      "aria-controls": `sales-product-tabpanel-${index}`,
    };
  }

  //Function to get the sum of all total amounts in orders
  const getTotalSalesInfo = () => {
    let sales = 0;
    let pretaxes = 0;
    let taxes = 0;

    for (const order of data) {
      sales += Number(order.billing.amount);
      pretaxes += Number(order.billing.pretax);
      taxes += Number(order.billing.taxes);
    }

    return {
      sales: Number(sales).toFixed(2),
      pretaxes: Number(pretaxes).toFixed(2),
      taxes: Number(taxes).toFixed(2),
    };
  };

  //Function to get total sales of product category
  const getTotalSalesOfProducts = () => {
    let stationsSales = [0, 0, 0]; // TOTAL / PRETAX / TAXES
    let productsSales = [0, 0, 0]; // TOTAL / PRETAX / TAXES

    for (const order of data) {
      if (evstations.includes(order.productName)) {
        stationsSales[0] += Number(order.billing.amount);
        stationsSales[1] += Number(order.billing.pretax);
        stationsSales[2] += Number(order.billing.taxes);
      } else {
        productsSales[0] += Number(order.billing.amount);
        productsSales[1] += Number(order.billing.pretax);
        productsSales[2] += Number(order.billing.taxes);
      }
    }

    return {
      stationsSales: Number(stationsSales[0]).toFixed(2),
      stationsPretax: Number(stationsSales[1]).toFixed(2),
      stationsTaxes: Number(stationsSales[2]).toFixed(2),

      productsSales: Number(productsSales[0]).toFixed(2),
      productsPretax: Number(productsSales[1]).toFixed(2),
      productsTaxes: Number(productsSales[2]).toFixed(2),
    };
  };

  //Function to display the finished orders of current month
  const getCurrentMonthInfo = () => {
    let MonthSales = 0;

    if (year == new Date().getFullYear()) {
      for (const order of data.filter((order) => {
        const dueDate = new Date(order.finishedAt);
        return dueDate.getMonth() === new Date().getMonth();
      })) {
        MonthSales += Number(order.billing.amount);
      }

      if (MonthSales === 0) {
        return typographyForText(`Sin ventas en ${selectMonth()}`);
      } else {
        return typographyForText(
          `+$${MonthSales.toFixed(2)} en ${selectMonth()}`,
          null,
          "#26D416"
        );
      }
    } else {
      return null;
    }
  };

  // ==========> Component <==========
  return (
    <Card raised sx={{ height: "100%" }}>
      <Grid container spacing={1} sx={{ padding: "15px" }}>
        <Grid item xs={12}>
          {typographyForSubtitles(`Ventas totales de ${year}`, "secondary")}
        </Grid>
        <Grid item xs={12}>
          <Stack>
            {typographyForMainTitles(`$ ${getTotalSalesInfo().sales}`)}
            {getCurrentMonthInfo()}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack justifyContent="flex-start" textAlign="left" spacing={0.5}>
            <Divider />
            {typographyForCustomizedFontSize(
              "Monto total de ventas sin IVA",
              "11px",
              null,
              "BOLD"
            )}
            {typographyForSubtitles(`$ ${getTotalSalesInfo().pretaxes}`)}
            {typographyForCustomizedFontSize(
              "Monto total por IVA",
              "11px",
              null,
              "BOLD"
            )}
            {typographyForSubtitles(`$ ${getTotalSalesInfo().taxes}`)}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Tabs
              centered
              indicatorColor="secondary"
              textColor="secondary"
              value={tabValue}
              onChange={changeTab}
              aria-label="sales-product-tabs"
            >
              <Tab
                label="Estaciones"
                sx={{ fontSize: "11px" }}
                {...a11yProps(0)}
              />
              <Tab
                label="Accesorios"
                sx={{ fontSize: "11px" }}
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>
        </Grid>
        <Grid item xs={12} align="center">
          <TabPanel value={tabValue} index={0}>
            {typographyForMainTitles(
              `$ ${getTotalSalesOfProducts().stationsSales}`
            )}
            <Stack justifyContent="flex-start" textAlign="left" spacing={0.5}>
              {typographyForCustomizedFontSize(
                "Monto total de ventas sin IVA",
                "11px",
                null,
                "BOLD"
              )}
              {typographyForSubtitles(
                `$ ${getTotalSalesOfProducts().stationsPretax}`
              )}
              {typographyForCustomizedFontSize(
                "Monto total por IVA",
                "11px",
                null,
                "BOLD"
              )}
              {typographyForSubtitles(
                `$ ${getTotalSalesOfProducts().stationsTaxes}`
              )}
            </Stack>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {typographyForMainTitles(
              `$ ${getTotalSalesOfProducts().productsSales}`
            )}
            <Stack justifyContent="flex-start" textAlign="left" spacing={0.5}>
              {typographyForCustomizedFontSize(
                "Monto total de ventas sin IVA",
                "11px",
                null,
                "BOLD"
              )}
              {typographyForSubtitles(
                `$ ${getTotalSalesOfProducts().productsPretax}`
              )}
              {typographyForCustomizedFontSize(
                "Monto total por IVA",
                "11px",
                null,
                "BOLD"
              )}
              {typographyForSubtitles(
                `$ ${getTotalSalesOfProducts().productsTaxes}`
              )}
            </Stack>
          </TabPanel>
        </Grid>
      </Grid>
    </Card>
  );
}
